import bannerBg from './assets/banner-bg.png';
import couponBg from './assets/coupon-bg.png';
import leftIcon from './assets/left-image.svg';
import rightIcon from './assets/right-image.svg';
import templateOneDemo from './assets/template-one-demo.png';
import { MediaKey, MediaSettings, TemplateDesign } from '../../types';

/** Id of the base template every other template is built from. */
export const BASE_TEMPLATE_ID = 'template-1';

export const BASE_TEMPLATE_NAME = 'Template 1 - Classic';

/** Screenshot of the base template with all components on. */
export const BASE_TEMPLATE_THUMBNAIL = templateOneDemo;

/** Built-in images used when a media item has no custom URL. */
export const BASE_MEDIA_ASSETS: Record< MediaKey, string > = {
	bgImage: bannerBg,
	leftIcon,
	rightIcon,
	couponBg,
	// No built-in image: the code box image is only used when chosen.
	couponBoxBg: '',
};

/** Padding field values: empty = the style's own padding. */
const autoPadding = (): Record< string, string > => ( {
	paddingTop: '',
	paddingRight: '',
	paddingBottom: '',
	paddingLeft: '',
} );

/** Margin field values: empty = no margin. */
const autoMargin = (): Record< string, string > => ( {
	marginTop: '',
	marginRight: '',
	marginBottom: '',
	marginLeft: '',
} );

/** Hover colors: empty = no change on hover. */
const noHover = (
	keys: string[] = [ 'hoverText', 'hoverBg' ]
): Record< string, string > =>
	keys.reduce( ( all, key ) => ( { ...all, [ key ]: '' } ), {} );

const autoMedia = (): MediaSettings => ( {
	url: '',
	width: 'Auto',
	height: 'Auto',
	borderRadius: '0px',
	isVisible: true,
} );

/**
 * Template 1 - Classic, from the Figma design "CST-Floating Autumn Sale!"
 * (file CUJ9JWym9t3dRudRiA6R45, node 10062:10106). All components on.
 */
export const createBaseTemplate = (): TemplateDesign => ( {
	id: BASE_TEMPLATE_ID,
	name: BASE_TEMPLATE_NAME,

	components: {
		background: {
			visible: true,
			variant: 'image',
			style: {
				color: '#FBE3CF',
				gradientFrom: '#FDEBDC',
				gradientTo: '#F6D3B6',
				gradientAngle: '90',
				height: '80',
				gap: '32',
				textGap: '',
				...autoPadding(),
			},
		},
		leftImage: {
			visible: true,
			variant: 'image',
			style: { offset: '20' },
		},
		rightImage: {
			visible: true,
			variant: 'image',
			style: { offset: '20' },
		},
		// Empty text = not shown until the admin types one.
		preText: {
			visible: true,
			variant: 'plain',
			style: {
				bgColor: '#FFFFFF',
				radius: '6',
				...autoPadding(),
				...autoMargin(),
				...noHover(),
				linkUnderline: 'hover',
			},
		},
		text: {
			visible: true,
			variant: 'plain',
			style: {
				bgColor: '#FFFFFF',
				radius: '6',
				...autoPadding(),
				...autoMargin(),
				...noHover(),
				linkUnderline: 'hover',
			},
		},
		// Third Text (component key kept for saved templates).
		headline: {
			visible: true,
			variant: 'plain',
			style: {
				bgColor: '#FFFFFF',
				radius: '6',
				// Old "Space between texts" (see background textGap).
				gap: '6',
				...autoPadding(),
				...autoMargin(),
				...noHover(),
				linkUnderline: 'hover',
			},
		},
		fourthText: {
			visible: true,
			variant: 'plain',
			style: {
				bgColor: '#FFFFFF',
				radius: '6',
				...autoPadding(),
				...autoMargin(),
				...noHover(),
				linkUnderline: 'hover',
			},
		},
		coupon: {
			visible: true,
			variant: 'ticket',
			style: {
				bgColor: '#A04A22',
				borderColor: '#FFFFFF',
				cardColor: '#FFFFFF',
				accentColor: '#006D5B',
				codeBgColor: '#E7E9F2',
				copyBgColor: '#FEF4DD',
				pillFrom: '#FF9A48',
				pillTo: '#DA3A00',
				...autoPadding(),
				...autoMargin(),
				...noHover( [
					'hoverBg',
					'hoverText',
					'hoverBorder',
					'hoverLabel',
					'hoverArea',
				] ),
				hoverEffect: 'dim',
				boxBgType: 'default',
				boxBgColor: '#ED7D32',
				boxGradFrom: '#ED7D32',
				boxGradTo: '#F49538',
				boxGradAngle: '90',
				boxBorderStyle: 'default',
				boxBorderColor: '#FFFFFF',
				boxBorderWidth: '1',
				boxRadius: '',
				boxWidth: '',
				boxHeight: '',
				areaBg: '',
				areaRadius: '',
				areaPaddingTop: '',
				areaPaddingRight: '',
				areaPaddingBottom: '',
				areaPaddingLeft: '',
			},
		},
		countdown: {
			visible: true,
			variant: 'text',
			style: {
				boxColor: '#AF222B',
				boxDigitColor: '#FFFFFF',
				tileColor: '#1BAA76',
				digitColor: '#F2F2F2',
				unitColor: '#2E4450',
				...autoPadding(),
				...autoMargin(),
			},
		},
		cta: {
			visible: true,
			variant: 'gradient',
			style: {
				gradientFrom: '#ED4500',
				gradientTo: '#BF090A',
				bgColor: '#BF090A',
				borderColor: '#BF090A',
				hoverBgColor: '#3F4D97',
				hoverTextColor: '#FFFFFF',
				glassFrom: '#00CCFF',
				glassTo: '#00FF3A',
				radius: '7.5',
				width: '150',
				height: '41',
				...autoPadding(),
				...autoMargin(),
				...noHover( [ 'hoverBg', 'hoverText', 'hoverBorder' ] ),
				icon: 'none',
				iconPosition: 'right',
				iconSize: '',
				iconGap: '8',
			},
		},
		closeButton: {
			visible: true,
			variant: 'icon',
			style: {
				color: '#042853',
				size: '14',
				...autoPadding(),
				...noHover( [ 'hoverText' ] ),
			},
		},
	},

	typography: {
		preText: {
			text: '',
			fontFamily: 'Poppins',
			weight: '500',
			size: '24',
			lineHeight: 'normal',
			color: '#2E4450',
			align: 'left',
			isVisible: true,
			link: '',
			openInNewTab: false,
		},
		extraText: {
			text: 'Autumn Sale!',
			fontFamily: 'Poppins',
			weight: '500',
			size: '24',
			lineHeight: 'normal',
			color: '#2E4450',
			align: 'left',
			isVisible: true,
			link: '',
			openInNewTab: false,
		},
		highlight: {
			text: 'Save 80%',
			fontFamily: 'Poppins',
			weight: '700',
			size: '24',
			lineHeight: 'normal',
			color: '#AF222B',
			align: 'left',
			isVisible: true,
			link: '',
			openInNewTab: false,
		},
		secondary: {
			text: 'On Single Courses.',
			fontFamily: 'Poppins',
			weight: '500',
			size: '24',
			lineHeight: 'normal',
			color: '#2E4450',
			align: 'left',
			isVisible: true,
			link: '',
			openInNewTab: false,
		},
		couponCopyLabel: {
			text: 'Copy Coupon Code',
			fontFamily: 'Lato',
			weight: '700',
			size: '12',
			lineHeight: '1',
			color: '#2E4450',
			align: 'center',
			isVisible: true,
		},
		couponCode: {
			text: 'AUTUMN80',
			fontFamily: 'Lato',
			weight: '700',
			size: '16',
			lineHeight: '1',
			color: '#FFFFFF',
			align: 'center',
			isVisible: true,
		},
		couponCopiedText: {
			text: 'COPIED!',
			fontFamily: 'Lato',
			weight: '700',
			size: '16',
			lineHeight: '1',
			color: '#FFFFFF',
			align: 'center',
			isVisible: true,
		},
		// Copy button inside the gradient pill coupon (Style 8).
		couponCopyButton: {
			text: 'COPY',
			fontFamily: 'Inter',
			weight: '600',
			size: '12',
			lineHeight: 'normal',
			color: '#FFFFFF',
			align: 'center',
			isVisible: true,
		},
		countdownLabel: {
			text: 'OFFER ENDS IN',
			fontFamily: 'Lato',
			weight: '700',
			size: '12',
			lineHeight: '1',
			color: '#2E4450',
			align: 'center',
			isVisible: true,
		},
		countdownDigits: {
			text: '',
			fontFamily: 'Poppins',
			weight: '600',
			size: '28',
			lineHeight: '1',
			color: '#AF222B',
			align: 'center',
			isVisible: true,
		},
		cta: {
			text: 'Explore More',
			fontFamily: 'Poppins',
			weight: '500',
			size: '18',
			lineHeight: '1',
			color: '#FFFFFF',
			align: 'center',
			isVisible: true,
		},
	},

	media: {
		bgImage: autoMedia(),
		leftIcon: autoMedia(),
		rightIcon: autoMedia(),
		couponBg: {
			...autoMedia(),
			width: '143px',
			height: '48px',
		},
		couponBoxBg: autoMedia(),
	},
} );
